import { useIsMutating, useMutation, UseMutationResult } from "@tanstack/react-query";
import { deleteClassroomSeatLayout, setClassroomSeatLayout, setClassroomTileWidth } from "@/js/api/personalisationApi";
import { SeatLayout } from "./seatLayout";

/**
 * Shared by every save button (the header bar and the floating unsaved-arrangement bar each hold
 * their own mutation), so a reset can tell whether any of them still has a write in flight.
 */
export const seatLayoutSaveMutationKey = (
	teacherStableIdV1: string | null | undefined,
	classroomStableIdV1: string | null | undefined
): unknown[] => ["seatLayoutSave", teacherStableIdV1 ?? null, classroomStableIdV1 ?? null];

export function useSaveSeatLayout(
	teacherStableIdV1: string | null | undefined,
	classroomStableIdV1: string | null | undefined
): UseMutationResult<object, unknown, SeatLayout> {
	return useMutation(
		async (layout: SeatLayout) => {
			if (!teacherStableIdV1 || !classroomStableIdV1) {
				throw new Error("Missing teacher or classroom id for seat layout save");
			}
			return setClassroomSeatLayout(teacherStableIdV1, classroomStableIdV1, layout);
		},
		{ mutationKey: seatLayoutSaveMutationKey(teacherStableIdV1, classroomStableIdV1) }
	);
}

/**
 * A reset deletes the layout a save is still writing, and the two requests can land server-side in
 * either order, which would recreate the arrangement the teacher just reset. Callers use this to
 * hold the reset back until the saves have settled.
 */
export function useIsSavingSeatLayout(
	teacherStableIdV1: string | null | undefined,
	classroomStableIdV1: string | null | undefined
): boolean {
	return useIsMutating({ mutationKey: seatLayoutSaveMutationKey(teacherStableIdV1, classroomStableIdV1) }) > 0;
}

export function useResetSeatLayout(
	teacherStableIdV1: string | null | undefined,
	classroomStableIdV1: string | null | undefined
): UseMutationResult<object, unknown, void> {
	return useMutation(async () => {
		if (!teacherStableIdV1 || !classroomStableIdV1) {
			throw new Error("Missing teacher or classroom id for seat layout reset");
		}
		return deleteClassroomSeatLayout(teacherStableIdV1, classroomStableIdV1);
	});
}

export interface SaveTileWidthVariables {
	tileWidth: number;
}

export function useSaveTileWidth(
	teacherStableIdV1: string | null | undefined,
	classroomStableIdV1: string | null | undefined
): UseMutationResult<object, unknown, SaveTileWidthVariables> {
	return useMutation(async ({ tileWidth }: SaveTileWidthVariables) => {
		if (!teacherStableIdV1 || !classroomStableIdV1) {
			throw new Error("Missing teacher or classroom id for tile width save");
		}
		return setClassroomTileWidth(teacherStableIdV1, classroomStableIdV1, tileWidth);
	});
}
